'use client';

import { useEffect } from 'react';
import Script from 'next/script';
import Icon from '@/core/icon';
import { ModalWrapper, useZusModalStore } from '@/helpers/modal.js';

// WEA-165 — Formulaire NATIF Brevo (double opt-in), exigé par Brevo pour la réactivation
// du compte d'envoi. Ne pas modifier l'attribut action du form ni les name des champs
// (EMAIL, email_address_check, locale) : la soumission casse. La structure HTML et les
// scripts sont repris de la page fournie ; seuls les TEXTES sont traduits par locale et
// l'habillage (layout du site, variables CSS héritées via .was-newsletter) est à nous.
// La réponse (succès/erreur) est affichée dans le modal du site : main.js de Brevo pose
// la classe sib-form-message-panel--active sur ses panneaux, un MutationObserver
// l'intercepte et ouvre le modal (les panneaux inline restent display:none).
const TEXTS = {
  fr: {
    headline: 'Restez dans la scène',
    intro: 'Nouveaux lieux, coulisses et actus de la Production : recevez la newsletter We Are Scene.',
    label: 'Votre adresse email',
    placeholder: 'prenom@exemple.com',
    spec: 'Ex. : prenom@exemple.com',
    button: "JE M'INSCRIS",
    consent: 'En vous inscrivant, vous acceptez de recevoir la newsletter et les actualités de We Are Scene. Vous pouvez vous désinscrire à tout moment via le lien présent dans chaque email.',
    errorPanel: "Nous n'avons pas pu confirmer votre inscription.",
    successPanel: "Presque fini ! Nous venons de vous envoyer un email : cliquez sur le lien qu'il contient pour confirmer votre inscription.",
    spamHint: "Si vous ne voyez pas notre email d'ici quelques minutes, pensez à vérifier votre dossier spam ou courrier indésirable.",
    invalid: 'Les informations que vous avez fournies ne sont pas valides. Veuillez vérifier le format du champ et réessayer.',
    required: 'Vous devez renseigner ce champ. ',
    requiredCode: 'Veuillez choisir un code pays',
    invalidDate: 'Veuillez saisir une date valide',
    requiredMultiselect: 'Veuillez choisir au moins une option',
    selectedList: '{quantity} liste sélectionnée',
    selectedLists: '{quantity} listes sélectionnées',
    selectedOption: '{quantity} sélectionné',
    selectedOptions: '{quantity} sélectionnés',
  },
  en: {
    headline: 'Stay in the scene',
    intro: 'New locations, behind the scenes and Production news: get the We Are Scene newsletter.',
    label: 'Your email address',
    placeholder: 'name@example.com',
    spec: 'E.g. name@example.com',
    button: 'SUBSCRIBE',
    consent: 'By subscribing, you agree to receive the We Are Scene newsletter and news. You can unsubscribe at any time via the link in each email.',
    errorPanel: 'We could not confirm your subscription.',
    successPanel: 'Almost done! We just sent you an email: click the link it contains to confirm your subscription.',
    spamHint: "If you don't see our email within a few minutes, please check your spam or junk folder.",
    invalid: 'The information provided is not valid. Please check the field format and try again.',
    required: 'This field cannot be left blank. ',
    requiredCode: 'Please choose a country code',
    invalidDate: 'Please enter a valid date',
    requiredMultiselect: 'Please choose at least one option',
    selectedList: '{quantity} list selected',
    selectedLists: '{quantity} lists selected',
    selectedOption: '{quantity} selected',
    selectedOptions: '{quantity} selected',
  },
  es: {
    headline: 'Quédate en la escena',
    intro: 'Nuevos espacios, entre bastidores y novedades de la Producción: recibe la newsletter de We Are Scene.',
    label: 'Tu dirección de email',
    placeholder: 'nombre@ejemplo.com',
    spec: 'Ej.: nombre@ejemplo.com',
    button: 'ME SUSCRIBO',
    consent: 'Al suscribirte, aceptas recibir la newsletter y las novedades de We Are Scene. Puedes darte de baja en cualquier momento mediante el enlace presente en cada email.',
    errorPanel: 'No hemos podido confirmar tu suscripción.',
    successPanel: '¡Casi listo! Te acabamos de enviar un email: haz clic en el enlace que contiene para confirmar tu suscripción.',
    spamHint: 'Si no ves nuestro email en unos minutos, revisa tu carpeta de spam o correo no deseado.',
    invalid: 'La información proporcionada no es válida. Por favor revisa el formato del campo y vuelve a intentarlo.',
    required: 'Este campo es obligatorio. ',
    requiredCode: 'Por favor elige un código de país',
    invalidDate: 'Por favor introduce una fecha válida',
    requiredMultiselect: 'Por favor elige al menos una opción',
    selectedList: '{quantity} lista seleccionada',
    selectedLists: '{quantity} listas seleccionadas',
    selectedOption: '{quantity} seleccionado',
    selectedOptions: '{quantity} seleccionados',
  },
};

const brevoFormHtml = (txt, locale) => `
    <!-- Begin Brevo Form -->
    <div class="sib-form" style="text-align:center;background-color:var(--tint);">
      <div id="sib-form-container" class="sib-form-container">
        <div id="error-message" class="sib-form-message-panel" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:16px;text-align:left;color:#661d1d;background-color:#ffeded;border-color:#ff4949;border-radius:8px;max-width:540px;">
          <div class="sib-form-message-panel__text sib-form-message-panel__text--center">
            <svg viewBox="0 0 512 512" class="sib-icon sib-notification__icon"><path d="M256 40c118.621 0 216 96.075 216 216 0 119.291-96.61 216-216 216-119.244 0-216-96.562-216-216 0-119.203 96.602-216 216-216m0-32C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm-11.49 120h22.979c6.823 0 12.274 5.682 11.99 12.5l-7 168c-.268 6.428-5.556 11.5-11.99 11.5h-8.979c-6.433 0-11.722-5.073-11.99-11.5l-7-168c-.283-6.818 5.167-12.5 11.99-12.5zM256 340c-15.464 0-28 12.536-28 28s12.536 28 28 28 28-12.536 28-28-12.536-28-28-28z"/></svg>
            <span class="sib-form-message-panel__inner-text">${txt.errorPanel}</span>
          </div>
        </div>
        <div></div>
        <div id="success-message" class="sib-form-message-panel" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:16px;text-align:left;color:#085229;background-color:#e7faf0;border-color:#13ce66;border-radius:8px;max-width:540px;">
          <div class="sib-form-message-panel__text sib-form-message-panel__text--center">
            <svg viewBox="0 0 512 512" class="sib-icon sib-notification__icon"><path d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 464c-118.664 0-216-96.055-216-216 0-118.663 96.055-216 216-216 118.664 0 216 96.055 216 216 0 118.663-96.055 216-216 216zm141.63-274.961L217.15 376.071c-4.705 4.667-12.303 4.637-16.97-.068l-85.878-86.572c-4.667-4.705-4.637-12.303.068-16.97l8.52-8.451c4.705-4.667 12.303-4.637 16.97.068l68.976 69.533 163.441-162.13c4.705-4.667 12.303-4.637 16.97.068l8.451 8.52c4.668 4.705 4.637 12.303-.068 16.97z"/></svg>
            <span class="sib-form-message-panel__inner-text">${txt.successPanel}</span>
          </div>
        </div>
        <div></div>
        <div id="sib-container" class="sib-container--large sib-container--vertical" style="max-width:540px;text-align:center;background-color:#FFFFFF;border-width:1px;border-style:solid;border-color:#E4DCFF;border-radius:14px;direction:ltr;">
          <form id="sib-form" method="POST" action="https://cf4689ea.sibforms.com/serve/MUIFACVilWYeubReRG3G1N7K1av9OsGqMtqymjxIyKBlsTjLs6sjK1o9TVoIpIQ8ytBIO78BtArQuvCZTCnhU0bz7SnobaIiGKFcDCOlhUJDN5DsoubVIX75rR2s8Ejk55FfM9KyZD83y3WnC_gyAif7fCwOnpxHLavbXn3MqNAYkKcAzpiD41xa5-tu76c6L4p6-q_LfwUqVXZKsw==" data-type="subscription">
            <div style="padding:8px 0;">
              <div class="sib-form-block" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:32px;font-weight:700;text-align:left;color:#1A1130;background-color:transparent;">
                <p style="margin:0;">${txt.headline}</p>
              </div>
            </div>
            <div style="padding:8px 0;">
              <div class="sib-form-block" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:16px;text-align:left;color:#4A4266;background-color:transparent;">
                <div class="sib-text-form-block">
                  <p style="margin:0;">${txt.intro}</p>
                </div>
              </div>
            </div>
            <div style="padding:8px 0;">
              <div class="sib-input sib-form-block">
                <div class="form__entry entry_block">
                  <div class="form__label-row ">
                    <label class="entry__label" style="font-weight:700;text-align:left;font-family:'Satoshi',Helvetica,sans-serif;font-size:16px;color:#1A1130;" for="EMAIL" data-required="*">${txt.label}</label>
                    <div class="entry__field">
                      <input class="input " type="text" id="EMAIL" name="EMAIL" autocomplete="off" value="" placeholder="${txt.placeholder}" data-required="true" required />
                    </div>
                  </div>
                  <label class="entry__error entry__error--primary" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:16px;text-align:left;color:#661d1d;background-color:#ffeded;border-color:#ff4949;border-radius:8px;"></label>
                  <label class="entry__specification" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:12px;text-align:left;color:#8A81A6;">${txt.spec}</label>
                </div>
              </div>
            </div>
            <div style="padding:8px 0;">
              <div class="sib-form-block" style="text-align:left">
                <button class="sib-form-block__button sib-form-block__button-with-loader" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:16px;font-weight:700;text-align:left;color:#FFFFFF;background-color:#7C4DFF;border-width:0px;border-radius:10px;padding:14px 24px;" form="sib-form" type="submit">
                  <svg class="icon clickable__icon progress-indicator__icon sib-hide-loader-icon" viewBox="0 0 512 512"><path d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z"/></svg>
                  ${txt.button}
                </button>
              </div>
            </div>
            <div style="padding:4px 0 8px 0;">
              <div class="sib-form-block" style="font-family:'Satoshi',Helvetica,sans-serif;font-size:12px;text-align:left;color:#8A81A6;">
                <p style="margin:0;">${txt.consent}</p>
              </div>
            </div>
            <input type="text" name="email_address_check" value="" class="input--hidden">
            <input type="hidden" name="locale" value="${locale}">
          </form>
        </div>
      </div>
    </div>
    <!-- End Brevo Form -->
`;

// La vraie réponse Brevo au submit contient un `redirect` vers LEUR page de confirmation
// hébergée : main.js fait window.top.location.replace(...) et on quitte le site — jamais
// de panneau, donc jamais de modal. On retire `redirect` de la réponse (XHR sibforms/serve
// uniquement) : main.js retombe alors sur son chemin « panneau actif », que le
// MutationObserver transforme en modal. Posé avant le chargement de main.js.
const BREVO_REDIRECT_INTERCEPT_JS = `
      (function () {
        var origOpen = XMLHttpRequest.prototype.open;
        var protoDesc = Object.getOwnPropertyDescriptor(XMLHttpRequest.prototype, 'response');
        XMLHttpRequest.prototype.open = function (method, url) {
          if (String(url).indexOf('sibforms.com/serve') !== -1) {
            var xhr = this;
            Object.defineProperty(this, 'response', {
              configurable: true,
              get: function () {
                var raw = protoDesc.get.call(xhr);
                try {
                  var data = JSON.parse(raw);
                  if (data && data.success && data.redirect) {
                    delete data.redirect;
                    return JSON.stringify(data);
                  }
                } catch (e) { /* réponse non-JSON : on la laisse telle quelle */ }
                return raw;
              },
            });
          }
          return origOpen.apply(this, arguments);
        };
      }());
`;

// Config attendue par main.js de Brevo (mêmes clés que la page fournie, textes par locale).
const brevoConfigJs = (txt, locale) => `
      window.REQUIRED_CODE_ERROR_MESSAGE = ${JSON.stringify(txt.requiredCode)};
      window.LOCALE = ${JSON.stringify(locale)};
      window.EMAIL_INVALID_MESSAGE = window.SMS_INVALID_MESSAGE = ${JSON.stringify(txt.invalid)};
      window.REQUIRED_ERROR_MESSAGE = ${JSON.stringify(txt.required)};
      window.GENERIC_INVALID_MESSAGE = ${JSON.stringify(txt.invalid)};
      window.INVALID_NUMBER = ${JSON.stringify(txt.invalid)};
      window.INVALID_DATE = ${JSON.stringify(txt.invalidDate)};
      window.REQUIRED_MULTISELECT_MESSAGE = ${JSON.stringify(txt.requiredMultiselect)};
      window.translation = {
        common: {
          selectedList: ${JSON.stringify(txt.selectedList)},
          selectedLists: ${JSON.stringify(txt.selectedLists)},
          selectedOption: ${JSON.stringify(txt.selectedOption)},
          selectedOptions: ${JSON.stringify(txt.selectedOptions)},
        }
      };
      var AUTOHIDE = Boolean(0);
`;

const NEWSLETTER_CSS = `
.was-newsletter{
  --violet:#7C4DFF;
  --violet-dark:#6A3DF0;
  --tint:transparent;
}
.was-newsletter #sib-container input::placeholder{font-family:'Satoshi',Helvetica,sans-serif;text-align:left;color:#B7ACD9;}
.was-newsletter #sib-container a{text-decoration:underline;color:var(--violet);}
.was-newsletter .sib-form-message-panel{display:none;}
.was-newsletter .sib-form-message-panel .sib-notification__icon{width:20px;height:20px;}
.was-newsletter #sib-form .sib-form-block__button{transition:background-color .15s ease;}
.was-newsletter #sib-form .sib-form-block__button:hover{background-color:var(--violet-dark) !important;}
`;

function openResultModal({ ok, text, spamHint }) {
  const { setModal1 } = useZusModalStore.getState();
  const close = () => setModal1(null);
  setModal1(
    <ModalWrapper onClose={close}>
      <div className="flex flex-col items-center justify-center w-full gap-4 px-10 bg-white min-h-dvh lg:min-h-0 lg:py-10 lg:rounded-2xl lg:max-w-md text-purple-6">
        <div className={`flex items-center justify-center w-16 h-16 rounded-full ${ok ? 'bg-green-100' : 'bg-red-light'}`}>
          <Icon name={ok ? 'check' : 'close'} className={`w-8 h-8 fill-current ${ok ? 'text-green-dark' : 'text-red'}`} />
        </div>
        <p className="m-0 text-lg text-center">{text}</p>
        {ok && spamHint ? <p className="m-0 text-sm text-center text-purple-6/60">{spamHint}</p> : null}
        <button type="button" className="px-8 py-2 mt-2 text-white transition-colors rounded-md bg-purple hover:bg-purple/80" onClick={close}>OK</button>
      </div>
    </ModalWrapper>,
  );
}

function NewsletterClient({ locale = 'en' }) {
  const txt = TEXTS[locale] || TEXTS.en;

  useEffect(() => {
    const panels = ['error-message', 'success-message']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    const observer = new MutationObserver(() => {
      panels.forEach((el) => {
        if (!el.classList.contains('sib-form-message-panel--active')) return;
        el.classList.remove('sib-form-message-panel--active');
        openResultModal({
          ok: el.id === 'success-message',
          text: el.querySelector('.sib-form-message-panel__inner-text')?.textContent || '',
          spamHint: txt.spamHint,
        });
      });
    });
    panels.forEach((el) => observer.observe(el, { attributes: true, attributeFilter: ['class'] }));
    // Ceinture : si Brevo répond un jour avec `successPage` (main.js remplace alors tout
    // le contenu de #sib-container, sans passer par les panneaux), on le traite en succès.
    const container = document.getElementById('sib-container');
    const containerObserver = new MutationObserver(() => {
      if (document.getElementById('sib-form')) return;
      containerObserver.disconnect();
      openResultModal({ ok: true, text: txt.successPanel, spamHint: txt.spamHint });
    });
    if (container) containerObserver.observe(container, { childList: true });
    return () => {
      observer.disconnect();
      containerObserver.disconnect();
    };
  }, [txt]);

  return (
    <div className="was-newsletter flex justify-center px-4 pt-28 pb-16 md:pt-36 min-h-[70vh]">
      <link rel="stylesheet" href="https://sibforms.com/forms/end-form/build/sib-styles.css" />
      <style>{NEWSLETTER_CSS}</style>
      <div className="w-full max-w-[560px]">
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: brevoFormHtml(txt, locale) }} />
      </div>
      <Script id="brevo-redirect-intercept" strategy="afterInteractive">{BREVO_REDIRECT_INTERCEPT_JS}</Script>
      <Script id="brevo-form-config" strategy="afterInteractive">{brevoConfigJs(txt, locale)}</Script>
      <Script src="https://sibforms.com/forms/end-form/build/main.js" strategy="afterInteractive" />
    </div>
  );
}

export default NewsletterClient;
